import * as Yup from 'yup';
import { useFormik, Form, FormikProvider } from 'formik';
import { useState } from 'react';
// @mui
import { Alert, Box, Button, IconButton, InputAdornment, Stack, TextField } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { Api } from '../../../utils/api';
import { ACCOUNT_URLS } from '../../../utils/api/urls';

// ----------------------------------------------------------------------

// The endpoint is unauthenticated by design: the user re-confirms with their
// own credentials rather than with a session token, so the payload carries
// `username`, `password` and the typed confirmation verbatim.
async function removeAccount({ username, password, verifydata }) {
  const formData = new FormData();
  formData.append('username', username.trim());
  formData.append('password', password);
  formData.append('verifydata', verifydata.trim());

  const response = await Api.unpost(ACCOUNT_URLS.delete, formData);

  return response;
}

// ----------------------------------------------------------------------

export default function DeleteAccForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [status, setStatus] = useState(null);

  // The confirmation has to match exactly, otherwise a typo reads as consent to
  // delete an account.
  const validationSchema = Yup.object().shape({
    username: Yup.string().trim().required('Username is required'),
    password: Yup.string().required('Password is required'),
    verifydata: Yup.string()
      .trim()
      .required('Type DELETE to confirm')
      .oneOf(['DELETE'], 'Type DELETE exactly, in capitals'),
  });

  const formik = useFormik({
    initialValues: {
      username: '',
      password: '',
      verifydata: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setStatus(null);

      try {
        await removeAccount(values);
        resetForm();
        setStatus({ type: 'success' });
      } catch (error) {
        console.error(error);
        // The API explains the common cases (wrong credentials, already gone);
        // fall back to plain copy when it sends nothing useful.
        const data = error?.response?.data;
        const text =
          (typeof data === 'string' ? data : data?.detail || data?.message || data?.error) ||
          'We could not delete your account. Please check your username and password, or contact us and we will do it for you.';

        setStatus({ type: 'error', text });
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { errors, touched, handleSubmit, getFieldProps, isSubmitting } = formik;

  return (
    <FormikProvider value={formik}>
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={2.5}>
          <TextField
            fullWidth
            name="username"
            label="Username"
            autoComplete="username"
            {...getFieldProps('username')}
            error={Boolean(touched.username && errors.username)}
            helperText={touched.username && errors.username}
          />

          <TextField
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            {...getFieldProps('password')}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            name="verifydata"
            label="Type DELETE to confirm"
            autoComplete="off"
            {...getFieldProps('verifydata')}
            error={Boolean(touched.verifydata && errors.verifydata)}
            helperText={
              touched.verifydata && errors.verifydata
                ? errors.verifydata
                : 'This is what stops the account being deleted by accident.'
            }
          />
        </Stack>

        {status?.type === 'success' && (
          <Alert severity="success" sx={{ mt: 3 }} onClose={() => setStatus(null)}>
            Your account has been deleted. We have removed your personal data from our systems.
          </Alert>
        )}

        {status?.type === 'error' && (
          <Alert severity="error" sx={{ mt: 3 }} onClose={() => setStatus(null)}>
            {status.text}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            type="submit"
            size="large"
            variant="contained"
            color="error"
            loading={isSubmitting}
            disabled={isSubmitting}
            sx={{ minWidth: { xs: '100%', sm: 200 } }}
          >
            Delete my account
          </Button>
        </Box>
      </Form>
    </FormikProvider>
  );
}
